import { Component } from '@theme/component';

/**
 * Superkraftklubbens hub, medlemmets hjem på /pages/superkraftklubben. Etappe A, 2026-09-13, bygde
 * skjelettet mot H1 (KONTRAKT.md, versjon 1). Etappe B, samme dag, fullførte flaten mot appens
 * kontraktpakke A1. A2, 2026-09-14, la til utløpsregelen og fraktgeografien etter eierens to valg
 * (snapshot `supreme-commander/briefer/klubben-a2-2026-09-14/`, `hub-kontrakt.server.ts` SHA-256
 * 38bdea07…, `KONTRAKT.md` SHA-256 91609749…, fixture SHA-256 c1af6848…). Appens B har koblet
 * A1/H2 i rutene lokalt, så A2-formen ER kontrakten fila er skrevet for; A1 og A2 er additive på
 * versjon 1.
 *
 * TO FORMER, BEGGE UTTRYKKELIGE. Et svar som bærer ALLE A2-feltene måles felt for felt, og et felt
 * med feil form er et avvik som blanker hele svaret. Et svar som bærer INGEN av dem er den
 * historiske H1-formen (ruta slik den svarte før etappe B), og den gir en uttrykkelig, SATSFRI
 * fallback (rettet 2026-09-14 etter Astra-reviewens contract-funn 1): et historisk regelsett gir
 * ingen satser, terskler, multiplikatorer, bonus, frakt eller utløpsregel, bare de satsfrie
 * setningene i Liquid og navigasjonen; en historisk status gir ingen personlige tall, siden den
 * ikke bærer appens publiseringssignal og lommeboka krever et uttrykkelig ja fra begge portene
 * (`#publisert`: `metafelter.publisert === true`, fravær er aldri et ja). Begge gir en
 * console.warn som sier «historisk». De to svarene er uavhengige: et gyldig A2-regelsett rendres
 * fullt selv om status er historisk, og en gyldig A2-lommebok slår aldri opp multiplikator eller
 * frakt i et historisk regelsett (det er `null` etter `#hentRegler`). Et svar som bærer BARE NOEN
 * av feltene er et avvik: fravær av et felt leses aldri som null eller som «ikke aktivert»
 * (`#reglerForm`, `#statusForm`). Fallbacken er ikke bevis for at ruta svarer H1; den er der så en
 * deploy-rekkefølge ikke blanker huben, og den lover aldri et tall på det grunnlaget.
 *
 * FIRE BESLUTTEDE TILSTANDER, avgjort i to lag. Serverside (Liquid i sections/skm-v2-klubb-hub.liquid):
 * `data-innlogget` (customer finnes) og `data-synket` (vakten skm-klubb-vakt sa ok, altså
 * contract_version 1 og last_synced_at satt). Klientside, fra GET /api/status: `medlem`,
 * `metafelter.publisert` og svarformen. Tilstand 2 og 4 kan ikke skilles serverside (begge
 * feiler vakten), og tilstand 1 kan ikke spørre status i det hele tatt. Derfor to lag.
 *
 *   salg          ikke innlogget. GET /api/regler alene. Satsfri salgsside; tallene fra regler
 *                 legges inn i egne linjer når svaret kommer, og uteblir ellers.
 *   innmelding    innlogget, status sier medlem: false. Løftet er at første bestilling melder deg
 *                 inn. Velkomstbonusens tall står bare når `regler.velkomstbonus` er non-null.
 *   lommebok      medlem, og BEGGE publiseringsportene sa ja: Liquid-vakten (feltene har vært
 *                 publisert til denne kunden) og appens `metafelter.publisert` (aldri usann igjen
 *                 etter første publisering). Tallene fra status, som er fasit (KONTRAKT.md:80).
 *                 Leder med kroner. `ventemelding` vises aldri her, og `metafelter.ferske` leses
 *                 aldri som noe annet enn form: den er synkindikasjon, ikke økonomisk kilde,
 *                 ikke autorisasjon og ikke bevis for at Shopify-cachen er fersk (Commander,
 *                 etappe B punkt 8).
 *   venter        medlem, men en av portene sa nei. Ved `publisert: false` uansett hva Liquid
 *                 sier (gammel cache kan se ok ut), og ved Liquid-nei uansett hva appen sier (en
 *                 medlems- eller aliasrad med historikk er ikke publisering til DENNE kunden).
 *                 Ingen tall, bare `ventemelding` fra appen, eller temaets egen satsfrie setning.
 *   utilgjengelig status svarte ikke, eller et svar brøt kontrakten. Egen tomtilstand uten tall
 *                 (KONTRAKT.md:114), aldri en statuskode. Ingen kode frigis, ingenting slettes.
 *
 * TEMAET REGNER ALDRI ET PROGRAMTALL (KONTRAKT.md del 3). Hvert tall på flaten kommer fra et svar og
 * formateres bare, med Intl for nb-NO. Prosentandelen er `Intl`-formatering av `andelstak`, ikke
 * regning. Begge avstandene (`tilNesteStopp`, `tilNesteNivaa`) kommer ferdig regnet i kroner å
 * handle for; null er «vises ikke», aldri 0 og aldri regnet her. Ingen sats står i Liquid-copyen;
 * T21-regelen gjelder, og T30 vokter denne fila.
 *
 * AKTIVERINGSPORTENE ER NULL/BOOLEAN I REGELPAKKEN, ikke en kontraktversjon. `velkomstbonus` null
 * betyr ingen bonus-setning. `nivaaer[].terskelKroner` null betyr ingen stige: nivånavnene står
 * som fakta, uten multiplikator og uten fordeler, siden en fordel uten vei er et løfte uten vei
 * (KONTRAKT.md:212). Bare hvert nivå med terskel gjør stigen til noe man kan gå opp.
 *
 * FRAKTFORDELEN: `#fraktEtikett` eier visningsvakten; fraktvedtaket 2026-09-14 med presiseringen
 * 2026-10-20 i docs/BESLUTNINGER.md eier ordlyden og innmeldingstidspunktet.
 *
 * UTLØPSREGELEN ER GENERELL OG KOMMER FRA `regler.utloep` (A2): dagtallet `opptjentePoengDager`
 * settes inn i setningen om nye poeng, `arvedePoengUtloeperAutomatisk` velger setningen om arven,
 * og begge typene nevnes siden én saldo kan bære begge. Ingen 365 står i temaet. Den PERSONLIGE
 * blokken `status.utloep` (flagg og `nesteUtloep`) er review-only og leses ikke: ingen individuell
 * utløpsdato, ingen «ingen av dine poeng utløper», ingen saldo-påstand (Commander, A2 punkt 2).
 *
 * FORBRUKET VISES IKKE. `forbruk.kvalifisertKroner` valideres i form, men rendres ikke: nivået
 * bæres av retensjonen og følger ikke forbruket ned (KONTRAKT.md:76), så et forbruk ved siden av
 * en terskel inviterer kunden til å regne et nivå appen ikke har regnet. Avstanden til neste nivå
 * er det tallet kunden kan handle på.
 *
 * SYNTETISKE TILSTANDER HØRER I HARNESSEN (scripts/qa/harness-klubb-hub.mjs), aldri her. Fila har
 * ingen demo-data og ingen `?tilstand=`-parameter.
 *
 * @typedef {Object} SkmKlubbHubRefs
 * @property {HTMLElement} status - Permanent live-region.
 */

const PROXY = '/apps/superkraftklubben';
const REGLER_URL = `${PROXY}/api/regler`;
const STATUS_URL = `${PROXY}/api/status`;
const KVITTER_URL = `${PROXY}/api/kvitter`;
/** Kontraktversjonen denne fila er skrevet for. Speiler krevd_versjon i snippets/skm-klubb-vakt.liquid. */
const KONTRAKT = '1';
/** Tidsfrist per kall. Samme størrelse som kurv-widgetens KALL_TIMEOUT, og av samme grunn. */
const KALL_TIMEOUT = 15000;
/**
 * Landnavn for ISO 3166-1 alpha-2 i `regler.frakt.land`. Kjenner temaet ikke navnet på et land i
 * lista, vises fordelen ikke i det hele tatt: en kode på skjermen er ikke et presist løfte, og et
 * utelatt land er det heller ikke.
 */
const LAND = Object.freeze({ NO: 'Norge', SE: 'Sverige', DK: 'Danmark', FI: 'Finland' });

const tall = new Intl.NumberFormat('nb-NO');
const prosent = new Intl.NumberFormat('nb-NO', { style: 'percent', maximumFractionDigits: 0 });
// Norsk tid, uansett hvor kunden leser: appen regner datoer og klokkeslett i norsk tid (losinn-løftet
// sier «kl. 15.04»), og «sikret til 31. juli» skal ikke bli 30. juli på en telefon i Boston.
const dato = new Intl.DateTimeFormat('nb-NO', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Europe/Oslo' });
const klokke = new Intl.DateTimeFormat('nb-NO', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Oslo' });

class KontraktBrudd extends Error {}

/** @param {unknown} verdi */
const esc = (verdi) =>
  String(verdi).replace(/[&<>"']/g, (tegn) => {
    switch (tegn) {
      case '&':
        return '&amp;';
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '"':
        return '&quot;';
      default:
        return '&#39;';
    }
  });
/** @param {unknown} n */
const erTall = (n) => typeof n === 'number' && Number.isFinite(n);
/** @param {unknown} s */
const erTekst = (s) => typeof s === 'string' && s.length > 0;
/** @param {number} n */
const kr = (n) => `${tall.format(n)}&nbsp;kr`;
/** @param {number} n */
const poeng = (n) => `${tall.format(n)}&nbsp;Kraftpoeng`;

/**
 * Temaets egne setninger. Satsfrie og tallfrie, hver eneste en: tallene kommer fra svarene og
 * settes inn av malene under, aldri skrevet her. Ordlyden er forslag til eiergjennomgangen
 * (SPORSMAL 163), ikke godkjent copy.
 */
const TEKST = {
  henter: 'Henter …',
  utilgjengelig: 'Vi fikk ikke kontakt med klubben nå. Prøv igjen om et øyeblikk.',
  kontraktBrudd: 'Vi får ikke vist klubben nå. Last siden på nytt.',
  // Tilstand 4 uten setning fra appen. Kontrakten sier at `ventemelding` er satt når feltene aldri
  // er skrevet, så denne skal normalt bare vises når Liquid-porten sa nei alene. Satsfri, tallfri.
  venterUtenSetning: 'Vi får ikke vist tallene dine her ennå.',
  kvittert: 'Meldingen er lukket.',
  kvitterFeilet: 'Vi fikk ikke lagret at du har lest meldingen. Prøv igjen om et øyeblikk.',
  kvitterKnapp: 'Den er lest',
  /** @param {string} n */
  opptjening: (n) => `Du får ${n}&nbsp;Kraftpoeng for hver krone du handler for.`,
  /**
   * Grensene for bruk som faktaliste med etikett og verdi i hver sin vekt, ikke én lang setning
   * (omkomponeringen 2026-09-22). Ren presentasjon: verdiene kommer ferdig formatert fra svaret,
   * etikettene er satsfrie ord.
   * @param {string} minste @param {string} trinn @param {string} hundre @param {string} andel
   */
  bruk: (minste, trinn, hundre, andel) =>
    [
      ['Hundre Kraftpoeng er', hundre],
      ['Minste beløp å bruke', minste],
      ['Velg beløp i trinn på', trinn],
      ['Poengene kan dekke', `inntil ${andel} av handlekurven`],
    ]
      .map(([etikett, verdi]) => `<div class="skm-klubb-hub__fakta-rad"><dt>${etikett}</dt><dd>${verdi}</dd></div>`)
      .join(''),
  /** @param {string[]} navn */
  nivaaer: (navn) => (navn.length > 1 ? `Nivåene heter ${navn.slice(0, -1).join(', ')} og ${navn[navn.length - 1]}.` : navn.length === 1 ? `Nivået heter ${navn[0]}.` : ''),
  /** @param {string} p @param {string} k */
  trinn: (p, k) => `${p} gir ${k}`,
  /** Etiketten over nivånavnet i statuscella. Satsfri, del av strukturen i #renderLommebok. */
  nivaaEtikett: 'Medlemsnivå',
  /** @param {string} m */
  multiplikator: (m) => `Det gir deg ${m} ganger poeng på det du handler.`,
  /** @param {string} kroner @param {string} stopp */
  avstand: (kroner, stopp) => `Handle for ${kroner} til, så kan du bruke ${stopp}.`,
  /** @param {string} kroner @param {string} navn */
  avstandNivaa: (kroner, navn) => `Handle for ${kroner} til, så er du ${navn}.`,
  /** @param {string} d */
  sikret: (d) => `Nivået er sikret til ${d}.`,
  /** @param {string} p @param {string} k */
  bonus: (p, k) => `Første bestilling gir deg ${p}, altså ${k}.`,
  /** @param {string} tid */
  retensjon: (tid) => `Et nivå du har nådd beholder du i minst ${tid}.`,
  /** @param {number} n */
  maaneder: (n) => `${tall.format(n)}&nbsp;${n === 1 ? 'måned' : 'måneder'}`,
  /** @param {string} kroner */
  stigeKrav: (kroner) => `fra ${kroner}`,
  stigeKravFoerste: 'etter første bestilling',
  /** @param {string} m */
  stigeMultiplikator: (m) => `${m} ganger poeng`,
  /** @param {string} etikett @param {string} land @param {boolean} gjelderNorge */
  fordelMedLand: (etikett, land, gjelderNorge) =>
    `${etikett} i ${land}${gjelderNorge ? (land === 'Norge' ? ' gjelder fra neste bestilling.' : '. Fri frakt i Norge gjelder fra neste bestilling.') : ''}`,
  /** @param {string[]} navn */
  landliste: (navn) => (navn.length > 1 ? `${navn.slice(0, -1).join(', ')} og ${navn[navn.length - 1]}` : navn[0] ?? ''),
  /** @param {number} n */
  dager: (n) => `${tall.format(n)}&nbsp;${n === 1 ? 'dag' : 'dager'}`,
  /** @param {string} tid */
  utloepOpptjent: (tid) => `Poeng du tjener nå gjelder i ${tid} fra du fikk dem.`,
  utloepArvUtenUtloep: 'Poeng som ble med fra den gamle klubben har ingen utløpsdato, og begge typene kan ligge i samme saldo.',
  utloepArvMedUtloep: 'Poeng som ble med fra den gamle klubben utløper også, og begge typene kan ligge i samme saldo.',
  /** @param {string} bundet */
  bundet: (bundet) => `${bundet} er bundet i en kode som ligger klar akkurat nå.`,
  /** @param {string} kode @param {string} verdi @param {string} kl */
  kode: (kode, verdi, kl) => `<strong>${kode}</strong> gir ${verdi} rabatt. Gjelder til kl. ${kl}.`,
  /** @param {string} kode @param {string} verdi */
  kodeUtenTid: (kode, verdi) => `<strong>${kode}</strong> gir ${verdi} rabatt.`,
};

/** Slotene regler-svaret eier. Tømmes samlet når svaret mangler. */
const REGLER_FELT = ['opptjening', 'bruk', 'nivaaer', 'nivaa-kort', 'stige', 'retensjon', 'trapp', 'loefte', 'bonus-hero', 'utloep-regel'];

/**
 * @extends {Component<SkmKlubbHubRefs>}
 */
class SkmKlubbHub extends Component {
  requiredRefs = ['status'];

  /** @type {'salg' | 'henter' | 'innmelding' | 'lommebok' | 'venter' | 'utilgjengelig'} */
  #tilstand = 'henter';
  /**
   * @type {any} Siste validerte A2-regelsett, eller null. Null også for den historiske H1-formen:
   * et historisk regelsett rendres ikke og slås aldri opp i (satsfri fallback), så en gyldig
   * lommebok kan ikke arve en multiplikator eller en fraktfordel fra det.
   */
  #regler = null;
  /** @type {any} Siste validerte status-svar, eller null. */
  #status = null;
  #kvitterer = false;
  /** @type {AbortController | null} */
  #henting = null;

  connectedCallback() {
    super.connectedCallback();
    this.#tilstand = this.#innlogget ? 'henter' : 'salg';
    this.#visTilstand(this.#tilstand);
    this.#hent();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#henting?.abort();
    this.#henting = null;
  }

  get #innlogget() {
    return this.hasAttribute('data-innlogget');
  }

  /** Vakten i Liquid sa ok: kontraktversjon 1 og last_synced_at satt. Publiseringshistorikken. */
  get #synket() {
    return this.hasAttribute('data-synket');
  }

  /** Etiketten for fraktfordelen, fra seksjonens settings. Tom betyr at fordelen ikke markeres. */
  get #fordelFrakt() {
    return this.getAttribute('data-fordel-frakt') || '';
  }

  // ---------------------------------------------------------------- nettverk

  /**
   * @param {number} ms
   * @param {AbortSignal} [signal]
   * @returns {{ signal: AbortSignal, rydd: () => void }}
   */
  #tidsfrist(ms, signal) {
    if (typeof AbortSignal.timeout === 'function' && typeof AbortSignal.any === 'function') {
      const frist = AbortSignal.timeout(ms);
      return { signal: signal ? AbortSignal.any([signal, frist]) : frist, rydd: () => {} };
    }
    const kontroll = new AbortController();
    const timer = setTimeout(() => kontroll.abort(new DOMException(`tidsavbrudd etter ${ms} ms`, 'TimeoutError')), ms);
    const rydd = () => clearTimeout(timer);
    if (signal) {
      const videre = () => {
        rydd();
        kontroll.abort(signal.reason);
      };
      if (signal.aborted) videre();
      else signal.addEventListener('abort', videre, { once: true });
    }
    return { signal: kontroll.signal, rydd };
  }

  /**
   * Ett kall mot app proxy med kontraktvakt: et vellykket svar uten riktig `kontrakt` er et brudd,
   * og da rendres ingen tall.
   * @param {string} url
   * @param {RequestInit} [init]
   * @returns {Promise<{ response: Response, data: any }>}
   */
  async #kall(url, init = {}) {
    const frist = this.#tidsfrist(KALL_TIMEOUT, init.signal);
    /** @type {Response} */
    let response;
    /** @type {any} */
    let data = null;
    try {
      response = await fetch(url, {
        credentials: 'same-origin',
        ...init,
        headers: { Accept: 'application/json', ...(init.headers || {}) },
        signal: frist.signal,
      });
      const tekst = await response.text();
      if (tekst) {
        try {
          data = JSON.parse(tekst);
        } catch (error) {
          data = null;
        }
      }
    } finally {
      frist.rydd();
    }
    if (response.ok && data && typeof data === 'object' && data.kontrakt !== KONTRAKT) {
      console.warn(`[skm-klubb-hub] Svaret fra ${url} bærer kontrakt ${JSON.stringify(data.kontrakt)}, temaet er skrevet for ${KONTRAKT}. Ingen tall rendres.`);
      throw new KontraktBrudd();
    }
    return { response, data };
  }

  async #hent() {
    this.#henting?.abort();
    const kontroll = new AbortController();
    this.#henting = kontroll;
    const { signal } = kontroll;

    const reglerLoefte = this.#hentRegler(signal);
    const statusLoefte = this.#innlogget ? this.#hentStatus(signal) : Promise.resolve(/** @type {any} */ ({ tilstand: 'salg' }));
    const [regler, status] = await Promise.all([reglerLoefte, statusLoefte]);
    if (signal.aborted) return;

    // Reglene først: lommeboka slår opp multiplikator og fraktfordel i dem.
    this.#regler = regler;
    this.#renderRegler(regler);

    this.#status = status.data ?? null;
    this.#tilstand = status.tilstand;
    if (this.#tilstand === 'lommebok') this.#renderLommebok(status.data);
    if (this.#tilstand === 'venter') this.#renderVenter(status.data);
    if (this.#tilstand === 'utilgjengelig') this.#visMelding(status.melding ?? TEKST.utilgjengelig, 'feil');
    this.#visTilstand(this.#tilstand);
  }

  /**
   * GET /api/regler. Krever ingen innlogget kunde. Feiler kallet, bryter svaret kontrakten, eller
   * har det den historiske H1-formen uten A2-feltene, er utfallet `null`: de satsfrie setningene
   * står, og tall-linjene uteblir. Det er tomtilstanden for salgssiden (KONTRAKT.md:114), ikke en
   * feil å vise. Den historiske formen skilles fra et avvik bare i advarselen: et regelsett uten
   * aktiveringsportene kan ikke si hvilke satser som er publisert, så ingen av dem rendres.
   * @param {AbortSignal} signal
   */
  async #hentRegler(signal) {
    try {
      const { response, data } = await this.#kall(REGLER_URL, { method: 'GET', signal });
      if (!response.ok || !data || typeof data !== 'object') {
        console.warn(`[skm-klubb-hub] ${REGLER_URL} svarte ${response.status}. Ingen satser rendres.`);
        return null;
      }
      const avvik = this.#reglerAvvik(data);
      if (avvik) {
        console.warn(`[skm-klubb-hub] Svaret fra ${REGLER_URL} er et kontraktavvik: ${avvik}. Ingen satser rendres.`);
        return null;
      }
      if (this.#reglerForm(data) === 'historisk_h1') {
        console.warn(`[skm-klubb-hub] ${REGLER_URL} svarte den historiske H1-formen uten A2-feltene. Satsfri fallback: ingen satser, terskler, multiplikatorer, bonus, frakt eller utløpsregel rendres; de satsfrie setningene og navigasjonen står.`);
        return null;
      }
      return data;
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return null;
      console.warn(`[skm-klubb-hub] ${REGLER_URL} feilet med ${error instanceof Error ? error.name : 'ukjent feil'}. Ingen satser rendres.`);
      return null;
    }
  }

  /**
   * Kontraktporten for regler. H1-feltene huben leser må finnes med riktig type; `nivaaer` kan
   * lovlig mangle helt (KONTRAKT.md: nivåtabellen feilkonfigurert), og da står opptjening og bruk.
   * A2-feltene måles etter formen (`#reglerForm`): finnes alle, måles hvert av dem, og feil form
   * er et avvik som blanker hele svaret, et halvt regelsett gir ingen halve satser. Finnes ingen,
   * er det den historiske H1-formen, og den slipper gjennom uten A2-felt. Finnes bare noen, er
   * det et avvik: fravær leses aldri som null eller som «ikke aktivert».
   * @param {any} d
   * @returns {string | null}
   */
  #reglerAvvik(d) {
    if (!d.opptjening || !erTall(d.opptjening.poengPerKrone)) return 'opptjening.poengPerKrone mangler eller er ikke et tall';
    const b = d.bruk;
    if (!b || typeof b !== 'object') return 'bruk mangler';
    for (const felt of ['minsteKroner', 'trinnKroner', 'kronerPerHundrePoeng', 'andelstak']) {
      if (!erTall(b[felt])) return `bruk.${felt} mangler eller er ikke et tall`;
    }
    if (!Array.isArray(b.stopp)) return 'bruk.stopp er ikke en liste';
    if (b.stopp.some((/** @type {any} */ s) => !erTall(s?.poeng) || !erTall(s?.kroner))) return 'et stopp mangler poeng eller kroner';
    if ('nivaaer' in d) {
      if (!Array.isArray(d.nivaaer)) return 'nivaaer er ikke en liste';
      for (const n of d.nivaaer) {
        if (!erTekst(n?.slug) || !erTekst(n?.navn)) return 'et nivå mangler slug eller navn';
      }
    }
    const form = this.#reglerForm(d);
    if (form === null) return 'svaret bærer bare noen av A2-feltene (velkomstbonus, retensjon, frakt, utloep, nivaaer[].terskelKroner og friFrakt), og et halvt regelsett gir ingen halve satser';
    if (form === 'historisk_h1') return null;
    for (const n of d.nivaaer ?? []) {
      if (n.terskelKroner !== null && !erTall(n.terskelKroner)) return 'et nivå har en terskelKroner som verken er tall eller null';
      if (typeof n.friFrakt !== 'boolean') return 'et nivå har en friFrakt som ikke er boolsk';
    }
    if (d.velkomstbonus !== null) {
      const v = d.velkomstbonus;
      if (!v || typeof v !== 'object' || !erTall(v.poeng) || !erTall(v.kroner)) return 'velkomstbonus er verken null eller {poeng, kroner}';
    }
    if (d.retensjon !== null) {
      if (!d.retensjon || typeof d.retensjon !== 'object' || !erTall(d.retensjon.maaneder)) return 'retensjon er verken null eller {maaneder}';
    }
    if (d.frakt !== null) {
      const f = d.frakt;
      if (!f || typeof f !== 'object' || !Array.isArray(f.land) || f.land.length === 0 || f.land.some((/** @type {any} */ l) => !/^[A-Z]{2}$/.test(String(l)))) {
        return 'frakt er verken null eller {land: [ISO 3166-1 alpha-2, minst ett]}';
      }
    }
    const u = d.utloep;
    if (!u || typeof u !== 'object' || !erTall(u.opptjentePoengDager) || u.opptjentePoengDager <= 0 || typeof u.arvedePoengUtloeperAutomatisk !== 'boolean') {
      return 'utloep har ikke opptjentePoengDager (et tall over null) og arvedePoengUtloeperAutomatisk (boolsk)';
    }
    return null;
  }

  /**
   * Formen regler-svaret har. `'a2'` når alle fire A2-nøklene finnes og hvert nivå bærer
   * `terskelKroner` og `friFrakt`; `'historisk_h1'` når INGEN av dem finnes; `null` når bare noen
   * finnes, som er et avvik. Uttrykkelig, så fravær aldri blir et oppdiktet null.
   * @param {any} d
   * @returns {'a2' | 'historisk_h1' | null}
   */
  #reglerForm(d) {
    const noekler = ['velkomstbonus', 'retensjon', 'frakt', 'utloep'];
    const har = noekler.filter((k) => k in d).length;
    const nivaaer = Array.isArray(d.nivaaer) ? d.nivaaer : [];
    const medA2 = nivaaer.filter((/** @type {any} */ n) => n && ('terskelKroner' in n || 'friFrakt' in n)).length;
    const alleA2 = nivaaer.every((/** @type {any} */ n) => n && 'terskelKroner' in n && 'friFrakt' in n);
    if (har === 0 && medA2 === 0) return 'historisk_h1';
    if (har === noekler.length && alleA2) return 'a2';
    return null;
  }

  /**
   * Formen status-svaret har, samme regel: `'a2'` når `avstand.tilNesteNivaa`, `nivaa.sikretTil`,
   * `forbruk` og `metafelter` alle finnes, `'historisk_h1'` når ingen finnes, `null` ellers.
   * `status.utloep` (A2, review-only) telles ikke og leses aldri.
   * @param {any} d
   * @returns {'a2' | 'historisk_h1' | null}
   */
  #statusForm(d) {
    const har =
      (d.avstand && typeof d.avstand === 'object' && 'tilNesteNivaa' in d.avstand ? 1 : 0) +
      (d.nivaa && typeof d.nivaa === 'object' && 'sikretTil' in d.nivaa ? 1 : 0) +
      ('forbruk' in d ? 1 : 0) +
      ('metafelter' in d ? 1 : 0);
    if (har === 0) return 'historisk_h1';
    if (har === 4) return 'a2';
    return null;
  }

  /**
   * Etiketten for fraktfordelen på ett nivå, eller tom streng når den ikke kan loves presist.
   * Krever tre ting samtidig: nivåets `friFrakt: true`, en landliste i `regler.frakt.land` (samme
   * aktiveringsport i appen), og at temaet kjenner navnet på hvert land i lista. Mangler ett av
   * dem, loves ingenting: aldri en generell «Fri frakt», og aldri et løfte fra lokal kode alene.
   * @param {any} nivaa
   * @returns {string}
   */
  #fraktEtikett(nivaa) {
    if (!nivaa || nivaa.friFrakt !== true || !this.#fordelFrakt) return '';
    const land = this.#regler?.frakt?.land;
    if (!Array.isArray(land) || land.length === 0) return '';
    const navn = land.map((/** @type {any} */ kode) => LAND[String(kode)]);
    if (navn.some((n) => !n)) {
      console.warn(`[skm-klubb-hub] Fraktfordelen gjelder land temaet ikke har navn på (${JSON.stringify(land)}). Fordelen vises ikke.`);
      return '';
    }
    return TEKST.fordelMedLand(esc(this.#fordelFrakt), TEKST.landliste(navn.map((n) => esc(n))), land.includes('NO'));
  }

  /**
   * GET /api/status, bare for innloggede. Avgjør tilstand 2 til 5.
   * @param {AbortSignal} signal
   * @returns {Promise<{ tilstand: 'salg' | 'innmelding' | 'lommebok' | 'venter' | 'utilgjengelig', data?: any, melding?: string }>}
   */
  async #hentStatus(signal) {
    try {
      const { response, data } = await this.#kall(STATUS_URL, { method: 'GET', signal });
      // Sesjonen kan ha gått ut mellom serverrenderingen og kallet: da er kunden ikke innlogget
      // lenger, og salgssiden er den sanne flaten.
      if (response.status === 401 && data?.feil === 'ikke_innlogget') return { tilstand: 'salg' };
      if (!response.ok || !data || typeof data !== 'object') {
        console.warn(`[skm-klubb-hub] ${STATUS_URL} svarte ${response.status}. Ingen tall rendres.`);
        return { tilstand: 'utilgjengelig' };
      }
      if (data.medlem === false) return { tilstand: 'innmelding', data };
      if (data.medlem !== true) {
        console.warn(`[skm-klubb-hub] Svaret fra ${STATUS_URL} er et kontraktavvik: medlem er ${JSON.stringify(data.medlem)}. Ingen tall rendres.`);
        return { tilstand: 'utilgjengelig', melding: TEKST.kontraktBrudd };
      }
      // Formen måles før noe avgjøres: et halvt svar gir verken tall eller en halv venting.
      const avvik = this.#statusAvvik(data);
      if (avvik) {
        console.warn(`[skm-klubb-hub] Svaret fra ${STATUS_URL} er et kontraktavvik: ${avvik}. Ingen tall rendres.`);
        return { tilstand: 'utilgjengelig', melding: TEKST.kontraktBrudd };
      }
      if (this.#statusForm(data) === 'historisk_h1') {
        console.warn(`[skm-klubb-hub] ${STATUS_URL} svarte den historiske H1-formen uten A1-feltene. Satsfri fallback: svaret bærer ikke appens publiseringssignal, så ingen personlige tall rendres og flaten venter.`);
      }
      // Tilstand 4 krever to ja. Appens `metafelter.publisert === true` (A1, APP-01) og
      // Liquid-vakten (publiseringshistorikk i denne kundens metafelter). Sier appen usann, hjelper
      // det ikke at en gammel Liquid-cache ser ok ut; sier Liquid nei, hjelper det ikke at en
      // medlems- eller aliasrad har historikk. Et H1-svar uten `metafelter` sier ingenting, og
      // ingenting er ikke et ja: Liquid-vakten avgjør aldri alene (rettet 2026-09-14).
      if (!this.#publisert(data) || !this.#synket) return { tilstand: 'venter', data };
      return { tilstand: 'lommebok', data };
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return { tilstand: 'utilgjengelig' };
      if (error instanceof KontraktBrudd) return { tilstand: 'utilgjengelig', melding: TEKST.kontraktBrudd };
      if (error instanceof DOMException && error.name === 'TimeoutError') {
        console.warn(`[skm-klubb-hub] ${STATUS_URL} ga tidsavbrudd etter ${KALL_TIMEOUT} ms. Ingen tall rendres.`);
      }
      return { tilstand: 'utilgjengelig' };
    }
  }

  /**
   * Appens publiseringssignal, og bare et uttrykkelig ja teller: `metafelter.publisert === true`.
   * Fravær er aldri et ja. Den historiske H1-formen (ingen `metafelter`, `#statusForm`) svarer
   * derfor usann, så lommeboka aldri åpnes av Liquid-vakten alene; en form som bærer noen men ikke
   * alle A1-felt er alt avvist i `#statusAvvik`. Til 2026-09-14 var bare `publisert: false` et
   * nei, og et svar uten feltet slapp gjennom på Liquid alene (Astra-reviewens contract-funn 1).
   * @param {any} d
   */
  #publisert(d) {
    return d.metafelter?.publisert === true;
  }

  /**
   * Kontraktporten for lommeboka. Måles FØR noe rendres, fordi et halvt svar ikke skal gi et halvt
   * tall. Tom liste er normaltilstand for både `historikk` og `aktiveKoder`; `null`, fravær og
   * feil form er avvik (samme skille som kurv-widgetens N1). `nivaa` kan være `{slug: null,
   * navn: null}`, og `avstand.tilNesteStopp`, `engangsmelding` og `ventemelding` kan være `null`
   * (KONTRAKT.md:115).
   *
   * PÅKREVDE NØKLER ETTER APPENS KONKRETE TYPER (rettet 2026-09-14, Astra-reviewens contract-funn
   * 2 og 3, `hub-kontrakt.server.ts` SHA-256 38bdea07…): `poeng.bundet` er et tall og aldri null;
   * `avstand.tilNesteStopp` må finnes og er objekt eller null; `nivaa.slug` og `nivaa.navn` må
   * begge finnes, hver tekst eller null, og de følges ad (begge fylt eller begge null). En
   * manglende nøkkel er ikke null. `historikk[].beskrivelse` er `string | null` etter typen og
   * ruta sender den uendret gjennom, så null er lovlig og skjuler ikke huben; nøkkelen må finnes,
   * og et objekt eller tall der er avvik som før.
   *
   * A1-feltene måles etter formen (`#statusForm`): finnes alle fire, måles hvert av dem
   * (`avstand.tilNesteNivaa` null eller de fire feltene, `nivaa.sikretTil` null eller tekst,
   * `forbruk` null eller `{kvalifisertKroner}`, `metafelter` med boolsk `publisert`, og boolsk
   * `ferske` om den er med; den leses ikke andre steder). Finnes ingen, er det den historiske
   * H1-formen. Finnes bare noen, er det et avvik. `status.utloep` (A2) er review-only og måles
   * ikke; den leses ingen steder i fila.
   * @param {any} d
   * @returns {string | null}
   */
  #statusAvvik(d) {
    if (!d.poeng || typeof d.poeng !== 'object' || !erTall(d.poeng.tilgjengelig)) return 'poeng.tilgjengelig mangler eller er ikke et tall';
    if (!erTall(d.poeng.bundet)) return 'poeng.bundet mangler eller er ikke et tall';
    if (!d.kroner || !erTall(d.kroner.tilgjengelig)) return 'kroner.tilgjengelig mangler eller er ikke et tall';
    if (!('nivaa' in d) || d.nivaa === null || typeof d.nivaa !== 'object') return 'nivaa mangler eller er ikke et objekt';
    if (!('slug' in d.nivaa) || !('navn' in d.nivaa)) return 'nivaa mangler slug eller navn';
    if (!(d.nivaa.slug === null || erTekst(d.nivaa.slug))) return 'nivaa.slug er verken tekst eller null';
    if (!(d.nivaa.navn === null || erTekst(d.nivaa.navn))) return 'nivaa.navn er verken tekst eller null';
    if ((d.nivaa.slug === null) !== (d.nivaa.navn === null)) return 'nivaa.slug og nivaa.navn følges ikke ad (begge fylt eller begge null)';
    if (this.#statusForm(d) === null) return 'svaret bærer bare noen av A1-feltene (avstand.tilNesteNivaa, nivaa.sikretTil, forbruk, metafelter)';
    if ('sikretTil' in d.nivaa && !(d.nivaa.sikretTil === null || typeof d.nivaa.sikretTil === 'string')) return 'nivaa.sikretTil er verken tekst eller null';
    if (!('avstand' in d) || d.avstand === null || typeof d.avstand !== 'object') return 'avstand mangler eller er ikke et objekt';
    if (!('tilNesteStopp' in d.avstand)) return 'avstand.tilNesteStopp mangler helt';
    const a = d.avstand.tilNesteStopp;
    if (a !== null) {
      if (!a || typeof a !== 'object' || !erTall(a.kronerAaHandle) || !erTall(a.stoppPoeng) || !erTall(a.stoppKroner)) {
        return 'avstand.tilNesteStopp er verken null eller de tre tallene';
      }
    }
    const n = d.avstand.tilNesteNivaa;
    if (n !== null && n !== undefined) {
      if (typeof n !== 'object' || !erTall(n.kronerAaHandle) || !erTekst(n.nivaaSlug) || !erTekst(n.nivaaNavn) || !erTall(n.terskelKroner)) {
        return 'avstand.tilNesteNivaa har ikke de fire feltene';
      }
    }
    if ('forbruk' in d && d.forbruk !== null) {
      if (!d.forbruk || typeof d.forbruk !== 'object' || !erTall(d.forbruk.kvalifisertKroner)) return 'forbruk.kvalifisertKroner mangler eller er ikke et tall';
    }
    if ('metafelter' in d) {
      const m = d.metafelter;
      if (!m || typeof m !== 'object' || typeof m.publisert !== 'boolean') return 'metafelter.publisert mangler eller er ikke boolsk';
      if ('ferske' in m && typeof m.ferske !== 'boolean') return 'metafelter.ferske er ikke boolsk';
    }
    if (!('engangsmelding' in d) || !(d.engangsmelding === null || typeof d.engangsmelding === 'string')) return 'engangsmelding er verken tekst eller null';
    if (!('ventemelding' in d) || !(d.ventemelding === null || typeof d.ventemelding === 'string')) return 'ventemelding er verken tekst eller null';
    if (!('historikk' in d)) return 'feltet historikk mangler helt';
    if (!Array.isArray(d.historikk)) return d.historikk === null ? 'historikk er null' : 'historikk er ikke en liste';
    if (d.historikk.some((/** @type {any} */ h) => !h || typeof h !== 'object' || !erTall(h.endring) || !('beskrivelse' in h) || !(h.beskrivelse === null || typeof h.beskrivelse === 'string'))) {
      return 'en hendelse mangler endring eller beskrivelse, eller beskrivelsen er verken tekst eller null';
    }
    if (!('aktiveKoder' in d)) return 'feltet aktiveKoder mangler helt';
    if (!Array.isArray(d.aktiveKoder)) return d.aktiveKoder === null ? 'aktiveKoder er null' : 'aktiveKoder er ikke en liste';
    if (d.aktiveKoder.some((/** @type {any} */ k) => !erTekst(k?.kode) || !erTall(k?.verdi))) return 'en kode mangler kode eller verdi';
    return null;
  }

  // ---------------------------------------------------------------- rendring

  /**
   * Én tilstand synlig om gangen. Hvert element med `data-for` lister tilstandene det hører til,
   * mellomromsdelt. Alt uten `data-for` står i alle tilstander. Innholdsstyrte sloter (`data-felt`)
   * ligger INNI et `data-for`-element når de trenger begge portene, aldri på samme element, siden
   * tilstandsbyttet ellers ville avdekket en tom slot.
   * @param {string} tilstand
   */
  #visTilstand(tilstand) {
    this.dataset.tilstand = tilstand;
    for (const el of this.querySelectorAll('[data-for]')) {
      const hoererTil = (el.getAttribute('data-for') || '').split(/\s+/).includes(tilstand);
      el.toggleAttribute('hidden', !hoererTil);
    }
    this.removeAttribute('aria-busy');
  }

  /**
   * @param {string} felt
   * @returns {HTMLElement | null}
   */
  #felt(felt) {
    const el = this.querySelector(`[data-felt="${felt}"]`);
    return el instanceof HTMLElement ? el : null;
  }

  /** Skriver i et felt og viser det; tom streng skjuler. @param {string} felt @param {string} html */
  #skriv(felt, html) {
    const el = this.#felt(felt);
    if (!el) return;
    el.innerHTML = html;
    el.toggleAttribute('hidden', html === '');
  }

  /** @param {any} r */
  #renderRegler(r) {
    if (!r) {
      for (const felt of REGLER_FELT) this.#skriv(felt, '');
      return;
    }
    const b = r.bruk;
    this.#skriv('opptjening', TEKST.opptjening(tall.format(r.opptjening.poengPerKrone)));
    this.#skriv('bruk', TEKST.bruk(kr(b.minsteKroner), kr(b.trinnKroner), kr(b.kronerPerHundrePoeng), prosent.format(b.andelstak)));
    const trinn = b.stopp
      .map((/** @type {any} */ s) => `<li class="skm-klubb-hub__trinn">${TEKST.trinn(poeng(s.poeng), `<strong>${kr(s.kroner)}</strong>`)}</li>`)
      .join('');
    this.#skriv('trapp', trinn);
    this.#skriv('loefte', erTekst(b.loefte) ? esc(b.loefte) : '');

    // Herfra er svaret alltid A2-formen: den historiske H1-formen er `null` alt i `#hentRegler`
    // og når ikke hit, så ingenting utledes av et fravær.

    // Velkomstbonusen: bare et non-null felt publiserer et tallfestet løfte (A1). Én flate
    // (omkomponeringen 2026-09-22): setningen står under leaden for salg og innmelding, der den
    // er relevant; medlemmer har alt fått bonusen, så steg-slotten er tatt ut av markupen.
    // Null gir ingen setning.
    const v = r.velkomstbonus;
    const bonus = v && erTall(v.poeng) ? TEKST.bonus(poeng(v.poeng), kr(v.kroner)) : '';
    this.#skriv('bonus-hero', bonus);

    // Tolvmånedersløftet som tall fra appen, aldri skrevet her.
    const rt = r.retensjon;
    this.#skriv('retensjon', rt && erTall(rt.maaneder) ? TEKST.retensjon(TEKST.maaneder(rt.maaneder)) : '');

    // Utløpsregelen (A2): generell, for begge poengtypene, med dagtallet fra svaret og aldri fra
    // temaet. Én flate fra 2026-09-22: Godt å vite (utloep-regel). Saldokortet lenker dit i
    // stedet for å gjenta regelen; sloten «utloep» har ingen flate og skrives ikke, og betyr
    // fortsatt det samme om den skulle få en flate igjen. Ingen personlig dato.
    const u = r.utloep;
    const utloep = u && erTall(u.opptjentePoengDager)
      ? `${TEKST.utloepOpptjent(TEKST.dager(u.opptjentePoengDager))} ${u.arvedePoengUtloeperAutomatisk === true ? TEKST.utloepArvMedUtloep : TEKST.utloepArvUtenUtloep}`
      : '';
    this.#skriv('utloep-regel', utloep);

    // Nivåene. Stigen med terskler bare når HVERT nivå bærer en terskel, altså når kjøpsleddet er
    // aktivert og klatringen finnes; da faller kontraktens forbud mot å vise multiplikatorene som
    // oppnåelige. Ellers navnene alene som i A: uten multiplikator og uten fordeler, siden en
    // fordel uten vei er et løfte uten vei (KONTRAKT.md:212).
    const nivaaer = Array.isArray(r.nivaaer) ? r.nivaaer : [];
    const stige = nivaaer.length > 0 && nivaaer.every((/** @type {any} */ n) => erTall(n.terskelKroner));
    if (stige) {
      this.#skriv('nivaaer', '');
      this.#skriv('nivaa-kort', '');
      this.#skriv('stige', nivaaer.map((/** @type {any} */ n) => this.#stigeRad(n)).join(''));
    } else {
      this.#skriv('stige', '');
      this.#skriv('nivaaer', TEKST.nivaaer(nivaaer.map((/** @type {any} */ n) => esc(n.navn))));
      // Navnene står også som kort side om side, samme flate som stigen. Kortene er pynt for
      // øyet (aria-hidden i markupen); setningen over bærer navnene for skjermlesere.
      this.#skriv('nivaa-kort', nivaaer.map((/** @type {any} */ n) => `<li class="skm-klubb-hub__nivaa-rad"><span class="skm-klubb-hub__nivaa-navn">${esc(n.navn)}</span></li>`).join(''));
    }
  }

  /**
   * Én rad i nivåstigen: navn, terskel i kroner fra svaret, og fordelene som svaret bærer.
   * Terskel 0 er laveste nivå, og der er kravet første bestilling, ikke «fra 0 kr».
   * @param {any} n
   */
  #stigeRad(n) {
    const krav = n.terskelKroner > 0 ? TEKST.stigeKrav(kr(n.terskelKroner)) : TEKST.stigeKravFoerste;
    const fordeler = [];
    if (erTall(n.multiplikator) && n.multiplikator !== 1) fordeler.push(TEKST.stigeMultiplikator(tall.format(n.multiplikator)));
    const frakt = this.#fraktEtikett(n);
    if (frakt) fordeler.push(frakt);
    return `<li class="skm-klubb-hub__nivaa-rad">
  <span class="skm-klubb-hub__nivaa-navn">${esc(n.navn)}</span>
  <span class="skm-klubb-hub__nivaa-krav">${krav}</span>
  ${fordeler.length > 0 ? `<span class="skm-klubb-hub__nivaa-fordeler">${fordeler.join(' · ')}</span>` : ''}
</li>`;
  }

  /** @param {any} d */
  #renderLommebok(d) {
    this.#skriv('kroner', kr(d.kroner.tilgjengelig));
    this.#skriv('poeng', poeng(d.poeng.tilgjengelig));
    this.#skriv('bundet', erTall(d.poeng.bundet) && d.poeng.bundet > 0 ? TEKST.bundet(poeng(d.poeng.bundet)) : '');

    // Nivået leses av status, aldri utledet av forbruket (KONTRAKT.md:76). Multiplikatoren og
    // fraktfordelen er egenskaper ved nivået medlemmet alt står på, fra regler-svaret, og vises
    // som fakta om det nivået: multiplikatoren bare når den skiller seg fra én, frakten bare når
    // appen sier `friFrakt: true` for nettopp dette nivået OG bærer landlista (`#fraktEtikett`).
    // `#regler` er null for et historisk regelsett, så ingen av dem lekker inn fra det.
    let nivaa = '';
    let fordel = '';
    if (erTekst(d.nivaa.navn)) {
      nivaa = `<span class="skm-klubb-hub__etikett">${TEKST.nivaaEtikett}</span> <strong class="skm-klubb-hub__nivaa-verdi">${esc(d.nivaa.navn)}</strong>`;
      const konfig = Array.isArray(this.#regler?.nivaaer) ? this.#regler.nivaaer.find((/** @type {any} */ n) => n.slug === d.nivaa.slug) : null;
      if (konfig && erTall(konfig.multiplikator) && konfig.multiplikator !== 1) {
        nivaa += ` <span class="skm-klubb-hub__nivaa-mult">${TEKST.multiplikator(tall.format(konfig.multiplikator))}</span>`;
      }
      const frakt = this.#fraktEtikett(konfig);
      if (frakt) fordel = `<span class="tag tag--brand">${frakt}</span>`;
    }
    this.#skriv('nivaa', nivaa);
    this.#skriv('nivaa-fordel', fordel);

    // «Sikret til» er datoen nivået faller aldri før, ikke en utløpsdato (A1). Null for laveste
    // nivå og for en dato appen ikke kjenner, og da står ingenting: ingen utløpsdato på Gjest, og
    // ingen gjettet dato. En dato som ikke lar seg lese vises heller ikke.
    const sikret = erTekst(d.nivaa.navn) && erTekst(d.nivaa.sikretTil) ? Date.parse(d.nivaa.sikretTil) : NaN;
    this.#skriv('sikret', Number.isFinite(sikret) ? TEKST.sikret(dato.format(new Date(sikret))) : '');

    // De to avstandene er to målinger (KONTRAKT.md:162): stoppet måles mot saldoen, nivået mot
    // kvalifisert forbruk. Begge kommer ferdig regnet i kroner å handle for. `null` betyr passert
    // siste stopp, siste nivå, ikke aktivert, ikke målt, eller at appen ikke kunne regne forsvarlig;
    // da står ingenting, aldri et gjettet tall og aldri «0 kroner til».
    const a = d.avstand.tilNesteStopp;
    this.#skriv('avstand', a ? TEKST.avstand(kr(a.kronerAaHandle), kr(a.stoppKroner)) : '');
    const n = d.avstand.tilNesteNivaa;
    this.#skriv('avstand-nivaa', n ? TEKST.avstandNivaa(kr(n.kronerAaHandle), esc(n.nivaaNavn)) : '');

    // Engangsmeldingen er ferdig norsk kundetekst fra appen, vist som den er, og lukkes med
    // POST /api/kvitter. Den vises bare når appen bærer den.
    const melding = this.#felt('engangsmelding');
    if (melding) {
      const tekst = this.#felt('engangsmelding-tekst');
      if (tekst) tekst.textContent = erTekst(d.engangsmelding) ? d.engangsmelding : '';
      melding.toggleAttribute('hidden', !erTekst(d.engangsmelding));
    }

    const koder = d.aktiveKoder
      .map((/** @type {any} */ k) => {
        const kl = Date.parse(k.utloeper ?? '');
        // Norsk klokkeslett skrives med punktum, «kl. 12.45», samme form som appens eget løfte
        // («Koden gjelder til kl. 15.04»). Intl gir kolon for nb-NO, så det byttes her.
        const tekst = Number.isFinite(kl) ? TEKST.kode(esc(k.kode), kr(k.verdi), klokke.format(new Date(kl)).replace(':', '.')) : TEKST.kodeUtenTid(esc(k.kode), kr(k.verdi));
        return `<li class="skm-klubb-hub__kode">${tekst}</li>`;
      })
      .join('');
    this.#skriv('koder', koder);
    const koderBlokk = this.#felt('koder-blokk');
    if (koderBlokk) koderBlokk.toggleAttribute('hidden', koder === '');

    // Beskrivelsen er `string | null` i kontrakten. Null (eller tom) gir en tom tekstcelle, aldri
    // ordet «null» og aldri en oppdiktet hendelsestype; beløpet og datoen står som for alle andre.
    const hendelser = d.historikk
      .map((/** @type {any} */ h) => {
        const naar = Date.parse(h.tidspunkt ?? '');
        const fortegn = h.endring > 0 ? '+' : h.endring < 0 ? '−' : '';
        return `<li class="skm-klubb-hub__hendelse">
  <span class="skm-klubb-hub__hendelse-tekst">${erTekst(h.beskrivelse) ? esc(h.beskrivelse) : ''}</span>
  <span class="skm-klubb-hub__hendelse-endring${h.endring < 0 ? ' skm-klubb-hub__hendelse-endring--minus' : ''}">${fortegn}${poeng(Math.abs(h.endring))}</span>
  ${Number.isFinite(naar) ? `<time class="skm-klubb-hub__hendelse-tid" datetime="${esc(new Date(naar).toISOString())}">${dato.format(new Date(naar))}</time>` : ''}
</li>`;
      })
      .join('');
    this.#skriv('historikk', hendelser);
    const tom = this.#felt('historikk-tom');
    if (tom) tom.toggleAttribute('hidden', hendelser !== '');
  }

  /** @param {any} d */
  #renderVenter(d) {
    this.#skriv('ventemelding', erTekst(d?.ventemelding) ? esc(d.ventemelding) : TEKST.venterUtenSetning);
  }

  /**
   * @param {string} tekst
   * @param {'feil' | 'ok' | ''} [type]
   */
  #visMelding(tekst, type = '') {
    const status = this.refs.status;
    if (!(status instanceof HTMLElement)) return;
    requestAnimationFrame(() => {
      status.className = `skm-klubb-hub__status${type ? ` skm-klubb-hub__status--${type}` : ''}`;
      status.textContent = tekst;
    });
  }

  // ---------------------------------------------------------------- handlinger (on:-attributter)

  /**
   * Lukk engangsmeldingen: POST /api/kvitter, idempotent. Meldingen forsvinner bare når appen har
   * bekreftet `kvittert: true`; ellers står den, og live-regionen sier hvorfor.
   * @param {Event} event
   */
  kvitter = async (event) => {
    event.preventDefault();
    if (this.#kvitterer) return;
    const knapp = event.target instanceof HTMLButtonElement ? event.target : null;
    this.#kvitterer = true;
    if (knapp) knapp.disabled = true;
    try {
      const { response, data } = await this.#kall(KVITTER_URL, { method: 'POST' });
      if (response.ok && data?.kvittert === true) {
        this.#felt('engangsmelding')?.setAttribute('hidden', '');
        this.#visMelding(TEKST.kvittert, 'ok');
        return;
      }
      this.#visMelding(TEKST.kvitterFeilet, 'feil');
    } catch (error) {
      if (error instanceof KontraktBrudd) {
        this.#visMelding(TEKST.kontraktBrudd, 'feil');
        return;
      }
      this.#visMelding(TEKST.kvitterFeilet, 'feil');
    } finally {
      this.#kvitterer = false;
      if (knapp) knapp.disabled = false;
    }
  };
}

/**
 * LEST MED VILJE IKKE. `status.utloep` (A2: `harPoengUtenUtloep`, `harPoengMedUtloep`,
 * `nesteUtloep`) går til uavhengig review og er ikke en bestilling til temaet. Fila leser ingen av
 * de tre, validerer ikke blokken, og bygger ingen personlig utløpsdato eller saldo-påstand av den.
 * Den generelle regelen kommer fra `regler.utloep`. Ingenting annet står på vent etter A2.
 */
const IKKE_LEST = Object.freeze(['status.utloep']);

if (!customElements.get('skm-klubb-hub')) {
  customElements.define('skm-klubb-hub', SkmKlubbHub);
}

export { IKKE_LEST };
